/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useCallback } from "react";
import logo from "../../../assets/images/pebble-logo.png";
import PopUpModal from "../../PopUpModal/PopUpModal";
import { useAuth } from "../../../context/AuthContext";
import MPSBusinessInfoFormPage1 from "./MPSBusinessInfoFormPage1";
import MPSBusinessInfoFormPage2 from "./MPSBusinessInfoFormPage2";
import MPSBusinessInfoFormPage3 from "./MPSBusinessInfoFormPage3";
import { useNavigate, Link } from "react-router-dom";
import { useMPSBusinessProfile } from "../../../context/MPSBusinessProfileContext";
import MPSSkipPopUpModal from "./MPSSkipModal";
import MPSCompleteProfileModal from "./MPSCompleteProfileModal";
import { Check } from "lucide-react";
import LegalModal from "../../ui/LegalModal";

const AutoSaveNotification = ({ visible }) => {
  return (
    <div 
      className={`fixed top-4 right-4 flex items-center gap-2 px-3 py-2 bg-grey-700 text-white text-xs rounded-lg shadow-lg transition-all duration-300 z-50 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
      }`}
    >
      <Check className="w-3 h-3" />
      <span>Progress saved</span>
    </div>
  );
};

const MPSSignUpForm = () => {
  const { setUserSignedUp, updateUserType } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [isLastStep, setIsLastStep] = React.useState(false);
  const [isFirstStep, setIsFirstStep] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const {
    submitMPSBusinessProfile,
    MPSBusinessProfileData,
  } = useMPSBusinessProfile();
  const [popUpModalType, setPopUpModalType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationType, setConfirmationType] = useState("confirmation");
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [showAutoSave, setShowAutoSave] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const saveProgress = useCallback(() => {
    const currentData = JSON.stringify(MPSBusinessProfileData);
    if (lastSavedDataRef.current !== currentData) {
      localStorage.setItem('mps_signup_draft', currentData);
      lastSavedDataRef.current = currentData;
      setShowAutoSave(true);
      setTimeout(() => setShowAutoSave(false), 2000);
    }
  }, [MPSBusinessProfileData]);

  useEffect(() => {
    autoSaveTimerRef.current = setInterval(() => {
      saveProgress();
    }, 10000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [saveProgress]);

  const handleOpen = (type = "confirmation") => {
    setOpenConfirmation(true);
    setConfirmationType(type);
  };
  const handleClose = () => {
    setOpenConfirmation(false);
  };

  const steps = [
    { label: "Your Business", isOptional: false },
    { label: "Profile & Socials", isOptional: false },
  ];
  const handleNext = () => {
    !isLastStep && setActiveStep((cur) => cur + 1);
    steps[activeStep].isCompleted = true;
  };

  const handleContinue = () => {
    saveProgress();
    handleNext();
  };

  const handlePrevious_step = () => {
    !isFirstStep && setActiveStep((cur) => cur - 1);
    steps[activeStep].isCompleted = true;
  };
  const handlePrevious = () => {
    saveProgress();
    handlePrevious_step();
  };
  const handleSkip = async () => {
    // Save and show skip confirmation popup
    handleOpen();
    setIsLoading(true);
    setPopUpModalType("skip");
    // Pre-submit the profile so "Go to Dashboard" is instant
    await submitMPSBusinessProfile();
    setIsLoading(false);
  };

  const handleMPSProfileSetupComplete = async () => {
    //upload the files and then save ht userProfile data
    handleOpen();
    setIsLoading(true);
    const response = await submitMPSBusinessProfile();
    if (response === true) {
      setPopUpModalType("complete");
      //open GoToDashboard popup
      console.log("Open COMPLETE GoToDashboard popup");
      setIsLoading(false);
    } else {
      setPopUpModalType("error");
    }
    //db Call to save the user DataStore
  };

  const handleGoToDashboard = async () => {
    try {
      // Pass 'MPS' explicitly to setUserSignedUp to avoid state timing issues
      const result = await setUserSignedUp(true, 'MPS');
      
      if (result && result.success) {
        handleClose();
        // Clear draft data only on success
        localStorage.removeItem('mps_signup_draft');
        // Navigate to MPS dashboard
        navigate("/mps/dashboard");
      } else {
        console.error('Failed to complete signup:', result?.message);
        // Still navigate but log the error
        handleClose();
        localStorage.removeItem('mps_signup_draft');
        navigate("/mps/dashboard");
      }
    } catch (error) {
      console.error('Error completing signup:', error);
      handleClose();
      navigate("/mps/dashboard");
    }
  };

  return (
    <div className={`transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
      <AutoSaveNotification visible={showAutoSave} />
      <PopUpModal
        open={openConfirmation}
        handleClose={handleClose}
        popUpModalType={popUpModalType}
      >
        {isLoading ? (
          <div className="flex flex-col items-center py-4 transition-opacity duration-300">
            <p className="text-sm text-grey-500">Setting up your profile...</p>
          </div>
        ) : popUpModalType === "complete" ? (
          // Complete profile setup
          <MPSCompleteProfileModal handleGoToDashboard={handleGoToDashboard} />
        ) : popUpModalType === "skip" ? (
          // skip and save profile information
          <MPSSkipPopUpModal
            handleClose={handleClose}
            handleGoToDashboard={handleGoToDashboard}
          />
        ) : confirmationType === "warning" ? (
          <div className="fixed inset-0 flex items-center justify-center  bg-opacity-100">
            <div className="bg-white justify-center p-6 rounded-2xl shadow-lg max-w-md w-full">
              <h2 className="justify-center text-center text-lg font-semibold mt-4 mb-6 text-grey-900">
                Are you sure you want to leave?
              </h2>
              <p className="mb-6 text-sm text-grey-600 text-center">
                You are in the middle of the signup process. If you leave now,
                youll need to update your information later in settings.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  className="bg-grey-25 hover:bg-grey-400 text-grey-900 font-semibold px-4 py-3 text-base rounded-lg border-2"
                  onClick={handleClose}
                >
                  Stay on this page
                </button>
                <button
                  className="bg-grey-700 hover:bg-grey-800 text-grey-25 px-4 py-3 text-base font-semibold rounded-lg"
                  onClick={() => {
                    handleClose();
                    window.location.href = "/";
                  }}
                >
                  Leave anyway
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </PopUpModal>
      <div className="min-h-screen bg-grey-25 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-[1000px] space-y-8 bg-white rounded-lg shadow-lg p-8">
          {/* Logo and Back Button */}
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => {
                saveProgress();
                if (activeStep === 0) {
                  window.location.href = "/signup";
                } else {
                  setActiveStep((cur) => cur - 1);
                }
              }}
              className="flex items-center text-grey-700 hover:text-[#B84141] transition-colors cursor-pointer z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back
            </button>
            <Link to="/">
              <img src={logo} alt="Pebble" className="h-12 w-auto mx-auto" />
            </Link>
            <div className="w-[72px]"></div>
          </div>

          {/* Minimal Step Indicator */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === activeStep 
                      ? 'w-8 bg-grey-900' 
                      : index < activeStep 
                        ? 'w-1.5 bg-grey-900' 
                        : 'w-1.5 bg-grey-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-grey-500">
              Step {activeStep + 1} of {steps.length}
              {steps[activeStep].isOptional && <span className="text-grey-400"> · Optional</span>}
            </p>
          </div>
          <div className="rounded-lg px-10 w-full">
            {activeStep === 0 && (
              <MPSBusinessInfoFormPage1 handleContinue={handleContinue} />
            )}
            {activeStep === 1 && (
              <MPSBusinessInfoFormPage2
                handleSkip={handleSkip}
                handleMPSProfileSetupComplete={handleMPSProfileSetupComplete}
              />
            )}
          </div>
        </div>
        {/* Footer */}
        <div className="mt-4 text-center">
          <p className="text-[10px] text-grey-500">
            By continuing, you agree to the{" "}
            <button 
              type="button"
              onClick={() => setLegalModal({ isOpen: true, type: 'terms' })}
              className="hover:text-pebble-red transition-colors"
            >
              Terms of Use
            </button>
            {" "}and{" "}
            <button 
              type="button"
              onClick={() => setLegalModal({ isOpen: true, type: 'privacy' })}
              className="hover:text-pebble-red transition-colors"
            >
              Privacy Policy
            </button>
          </p>
          <p className="text-[9px] text-grey-400 mt-1">
            &copy; Pebble The App, Inc. All rights reserved.
          </p>
        </div>
      </div>
      
      <LegalModal 
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ isOpen: false, type: null })}
        type={legalModal.type}
      />
    </div>
  );
};

export default MPSSignUpForm;
